import dayjs from 'dayjs'
import type { Vehicle, VehicleAdjustment } from '../types'

export const RENT_PERIOD_OPTIONS = [
  { value: 'monthly', label: '月付' },
  { value: 'quarterly', label: '季付' },
  { value: 'halfyear', label: '半年付' },
  { value: 'yearly', label: '年付' },
  { value: 'custom', label: '自定义' },
]

export const formatPeriodLabel = (period?: string, months?: number) => {
  if (period === 'custom') {
    const value = Number(months || 0)
    const safeMonths = value > 0 ? value : 1
    return `自定义(${safeMonths}个月)`
  }
  return RENT_PERIOD_OPTIONS.find(opt => opt.value === period)?.label || period || '-'
}

export type ReceiptRecord = {
  _id?: string
  id?: string
  vehicleId?: string
  plate?: string
  model?: string
  company?: string
  customerName?: string
  amount?: number
  actualAmount?: number
  actualAmountDraft?: number
  verifiedAmount?: number
  dueDate?: string
  startDate?: string
  rentPeriod?: string
  status?: string
  notes?: string
  evidenceUrls?: string[]
  periodKey?: string
  depositAmount?: number
  accountingScenario?: string
  tradeInAmount?: number
  depositOffsetAmount?: number
  otherIncomeAmount?: number
  paymentDate?: string
}

export type PreviewItem = {
  vehicle: Vehicle
  amount: number
  actualAmount?: number
  actualAmountDraft?: number
  depositAmount?: number
  tailCollection?: boolean
  receiptType?: 'rent' | 'deposit_only' | 'rent_with_deposit'
  dueDate?: string
  startDate?: string
  rentPeriod?: string
  rentPeriodMonths?: number
  chargeRangeStart?: string
  chargeRangeEnd?: string
  outstanding: number
  rangeValidation?: {
    level: 'ok' | 'warning' | 'error'
    messages: string[]
    previousRangeEnd?: string
    expectedRangeStart?: string
  }
}

const paidStatuses = ['cashier_done', 'accounted', 'completed']
const receivableExemptCompanies = ['万利', '万利新材料']

const normalizeCompanyName = (name?: string) =>
  String(name || '')
    .trim()
    .replace(/\s+/g, '')
    .replace(/[()（）]/g, '')

export const isReceivableExemptVehicle = (vehicle?: Vehicle) => {
  const company = normalizeCompanyName(vehicle?.company)
  if (!company) return false
  if (company === '万利') return true
  if (company.includes('万利新材料')) return true
  return false
}

export const isReceivableVehicleStatus = (status?: string) => {
  const value = String(status || '').trim()
  return value === 'rented' || value === 'contracted'
}

export const isReceivableVehicle = (vehicle?: Vehicle) =>
  isReceivableVehicleStatus(vehicle?.status)

export const getVehicleDueDate = (vehicle: Vehicle, asOf = dayjs()) => {
  if (vehicle.reminderDate) return vehicle.reminderDate
  const startDate = vehicle.startDate ? dayjs(vehicle.startDate) : null
  if (!startDate || !startDate.isValid()) return vehicle.dueDate
  const months = getReminderMonths(vehicle)
  if (!months) return vehicle.dueDate
  const today = asOf.startOf('day')
  let due = startDate.startOf('day')
  let guard = 0
  while (due.isBefore(today, 'day') && guard < 2400) {
    due = due.add(months, 'month')
    guard += 1
  }
  if (guard >= 2400) return vehicle.dueDate || startDate.format('YYYY-MM-DD')
  const contractEnd = vehicle.dueDate ? dayjs(vehicle.dueDate) : null
  if (contractEnd && contractEnd.isValid() && contractEnd.isBefore(due, 'day')) {
    return contractEnd.format('YYYY-MM-DD')
  }
  return due.format('YYYY-MM-DD')
}

export const matchVehicle = (item: any, vehicle: Vehicle) => {
  const id = vehicle._id || vehicle.id || vehicle.plate
  const idStr = String(id || '')
  const plateStr = String(vehicle.plate || '')
  return String(item.vehicleId || '') === idStr || String(item.plate || '') === plateStr
}

export const isPaidItem = (receiptStatus: string | undefined, itemStatus: string | undefined) => {
  return paidStatuses.includes(receiptStatus || '') || paidStatuses.includes(itemStatus || '')
}

export const hasExplicitChargeRange = (item?: any, receipt?: any) => {
  const explicitFlag = item?.hasExplicitChargeRange ?? receipt?.hasExplicitChargeRange
  if (explicitFlag !== undefined) return Boolean(explicitFlag)
  const start = String(item?.chargeRangeStart ?? receipt?.chargeRangeStart ?? '').trim()
  const end = String(item?.chargeRangeEnd ?? receipt?.chargeRangeEnd ?? '').trim()
  return Boolean(start && end)
}

export const getAdjustmentEffect = (vehicle: Vehicle, adjustments: VehicleAdjustment[] = []) => {
  if (!adjustments.length) {
    return { outstandingDelta: 0, revenueDelta: 0 }
  }
  const relevant = adjustments
    .filter(adj => adj.type === 'parts' || adj.type === 'repair')
    .filter(adj => matchVehicle(adj, vehicle))
  const deductions = relevant.reduce((sum, adj) => sum + (Number(adj.amount) || 0), 0)
  const revenueDelta = -deductions
  const outstandingDelta = -deductions
  return { outstandingDelta, revenueDelta }
}

export const getAdjustmentEffectAtDate = (
  vehicle: Vehicle,
  adjustments: VehicleAdjustment[] = [],
  asOf?: dayjs.Dayjs
) => {
  if (!adjustments.length) {
    return { outstandingDelta: 0, revenueDelta: 0 }
  }
  const cutoff = asOf?.isValid() ? asOf.endOf('day') : null
  const relevant = adjustments
    .filter(adj => adj.type === 'parts' || adj.type === 'repair')
    .filter(adj => matchVehicle(adj, vehicle))
    .filter(adj => {
      if (!cutoff) return true
      const t = dayjs(adj.effectiveDate || adj.createdAt)
      return !t.isValid() || !t.isAfter(cutoff, 'day')
    })
  const deductions = relevant.reduce((sum, adj) => sum + (Number(adj.amount) || 0), 0)
  const revenueDelta = -deductions
  const outstandingDelta = -deductions
  return { outstandingDelta, revenueDelta }
}

export const getAdjustmentRevenueDelta = (adjustments: VehicleAdjustment[] = []) =>
  adjustments
    .filter(adj => adj.type === 'parts' || adj.type === 'repair')
    .reduce((sum, adj) => sum + (Number(adj.amount) || 0), 0) * -1

export const PAID_RECEIPT_STATUSES = new Set(['cashier_done', 'accounted', 'completed'])

export const getReceiptItemsSafe = (receipt: any): any[] => {
  const raw = receipt?.items
  if (Array.isArray(raw)) return raw
  if (raw && typeof raw === 'object') return Object.values(raw)
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) return parsed
      if (parsed && typeof parsed === 'object') return Object.values(parsed)
    } catch {
      return []
    }
  }
  return []
}

const getReceiptKey = (receipt: any) => String(receipt?._id || receipt?.id || '').trim()

export const getReceiptActualIncome = (receipt: any) => {
  const items = getReceiptItemsSafe(receipt)
  const itemsSum = items.reduce((sum: number, it: any) => {
    const val = Number(it?.actualAmount ?? it?.receivedAmount ?? it?.verifiedAmount ?? it?.amount ?? 0) || 0
    return sum + val
  }, 0)
  return Number(receipt?.actualAmount ?? receipt?.receivedAmount ?? receipt?.verifiedAmount ?? itemsSum ?? receipt?.amount ?? 0) || 0
}

export const getPaidReceiptDocs = (receipts: any[] = []) => {
  const filtered = receipts.filter((r: any) => PAID_RECEIPT_STATUSES.has(String(r?.status || '')))
  const uniq = new Map<string, any>()
  filtered.forEach((r: any) => {
    const key = getReceiptKey(r)
    if (!key) return
    if (!uniq.has(key)) uniq.set(key, r)
  })
  return Array.from(uniq.values())
}

export const getPaidReceiptActualTotal = (receipts: any[] = []) =>
  getPaidReceiptDocs(receipts).reduce((sum: number, r: any) => sum + getReceiptActualIncome(r), 0)

export const getMonthsByPeriod = (record: any) => {
  const period = record?.rentPeriod || 'monthly'
  const customMonths = Number(record?.rentPeriodMonths || 0)
  switch (period) {
    case 'quarterly':
      return 3
    case 'halfyear':
      return 6
    case 'yearly':
      return 12
    case 'custom':
      return customMonths > 0 ? customMonths : 1
    default:
      return 1
  }
}

const getReminderMonths = (vehicle: Vehicle) => {
  const reminderPeriod = (vehicle as any).reminderPeriod
  if (reminderPeriod) {
    return getMonthsByPeriod({
      rentPeriod: reminderPeriod,
      rentPeriodMonths: (vehicle as any).reminderPeriodMonths
    })
  }
  return getMonthsByPeriod(vehicle)
}

export const getMonthlyAmount = (record: any) => {
  const reminderAmountNumber = Number(record?.reminderAmount)
  if (Number.isFinite(reminderAmountNumber) && reminderAmountNumber > 0) {
    const reminderMonths = getMonthsByPeriod({
      rentPeriod: record?.reminderPeriod || record?.rentPeriod || 'monthly',
      rentPeriodMonths: record?.reminderPeriodMonths ?? record?.rentPeriodMonths
    })
    return reminderAmountNumber / reminderMonths
  }
  const rawRent = record?.rentAmount
  const rentNumber = Number(rawRent)
  const rent = Number.isFinite(rentNumber) ? rentNumber : parseFloat(String(rawRent || 0))
  if (!rent) return 0
  return rent / getMonthsByPeriod(record)
}

const getReceivableMode = (record: any): 'daily_365' | 'monthly_plan' => {
  const mode = String(record?.receivableMode || '').trim()
  if (mode === 'monthly_plan') return 'monthly_plan'
  return 'daily_365'
}

const getReceivableMonths = (record: any) => {
  const raw = Number(record?.receivableMonths || 0)
  if (Number.isFinite(raw) && raw > 0) return raw
  return 12
}

const getAnnualReceivableAmount = (record: any) => {
  const rentNumber = Number(record?.rentAmount)
  if (Number.isFinite(rentNumber) && rentNumber > 0) {
    // rentAmount is the periodic amount (monthly/quarterly/yearly etc.)
    // Convert to annual based on rentPeriod
    const periodMonths = getMonthsByPeriod(record)
    return rentNumber * (12 / periodMonths)
  }

  const reminderAmount = Number(record?.reminderAmount)
  if (Number.isFinite(reminderAmount) && reminderAmount > 0) {
    const reminderMonths = getMonthsByPeriod({
      rentPeriod: record?.reminderPeriod || record?.rentPeriod || 'monthly',
      rentPeriodMonths: record?.reminderPeriodMonths ?? record?.rentPeriodMonths
    })
    return (reminderAmount / Math.max(reminderMonths, 1)) * 12
  }
  return 0
}

const getRangeExpectedByRecord = (record: any, rangeStart: dayjs.Dayjs, rangeEnd: dayjs.Dayjs) => {
  if (rangeEnd.isBefore(rangeStart, 'day')) return 0

  const mode = getReceivableMode(record)
  const annual = getAnnualReceivableAmount(record)
  if (!annual) return 0

  if (mode === 'daily_365') {
    const days = rangeEnd.diff(rangeStart, 'day') + 1
    return (annual / 365) * Math.max(days, 0)
  }

  const months = Math.max(getReceivableMonths(record), 1)
  const monthly = annual / months
  let sum = 0
  let cursor = rangeStart.clone()
  while (cursor.isBefore(rangeEnd, 'day') || cursor.isSame(rangeEnd, 'day')) {
    const monthStart = cursor.startOf('month')
    const monthEnd = monthStart.endOf('month')
    const endThisSlice = monthEnd.isBefore(rangeEnd, 'day') ? monthEnd : rangeEnd
    const daysInMonth = monthStart.daysInMonth()
    const days = endThisSlice.diff(cursor, 'day') + 1
    const daily = monthly / daysInMonth
    sum += daily * days
    cursor = endThisSlice.add(1, 'day')
  }
  return sum
}

export const getActiveLeaseStart = (vehicle: Vehicle) => {
  const currentId = (vehicle as any).currentLeaseId
  const history = Array.isArray((vehicle as any).leaseHistory) ? (vehicle as any).leaseHistory : []
  const currentLease = currentId ? history.find((l: any) => l.leaseId === currentId) : null
  const pickDate = (raw: any) => {
    const d = dayjs(String(raw || '').trim())
    return d.isValid() ? d.startOf('day') : null
  }

  const leaseStart = pickDate(currentLease?.startDate)
  if (leaseStart) return leaseStart.format('YYYY-MM-DD')

  const openLease = history
    .filter((l: any) => !String(l?.endDate || '').trim())
    .map((l: any) => pickDate(l?.startDate))
    .filter(Boolean)
    .sort((a: any, b: any) => dayjs(a).valueOf() - dayjs(b).valueOf())
    .at(-1)
  if (openLease) return dayjs(openLease).format('YYYY-MM-DD')

  const vehicleStart = pickDate(vehicle.startDate)
  const pricingDates = (Array.isArray((vehicle as any).pricingHistory) ? (vehicle as any).pricingHistory : [])
    .map((p: any) => pickDate(p?.startDate))
    .filter(Boolean)
    .sort((a: any, b: any) => dayjs(a).valueOf() - dayjs(b).valueOf())

  if (vehicleStart && pricingDates.length) {
    const nearestNotAfterStart = [...pricingDates]
      .reverse()
      .find((d: any) => !dayjs(d).isAfter(vehicleStart, 'day'))
    if (nearestNotAfterStart) {
      const diff = vehicleStart.diff(dayjs(nearestNotAfterStart), 'day')
      // Only tolerate tiny drift between vehicle startDate and pricingHistory.
      // Large gaps usually mean stale pricing data from an older lease, which would overstate outstanding.
      if (diff >= 0 && diff <= 7) {
        return dayjs(nearestNotAfterStart).format('YYYY-MM-DD')
      }
    }
    return vehicleStart.format('YYYY-MM-DD')
  }

  if (vehicleStart) return vehicleStart.format('YYYY-MM-DD')
  if (pricingDates.length) return dayjs(pricingDates[pricingDates.length - 1]).format('YYYY-MM-DD')
  return new Date().toISOString()
}

export const isLeaseStarted = (vehicle: Vehicle, asOf = dayjs()) => {
  const start = dayjs(getActiveLeaseStart(vehicle))
  if (!start.isValid()) return true
  return !start.isAfter(asOf, 'day')
}

export const getPricingRecords = (vehicle: Vehicle) => {
  const history = Array.isArray((vehicle as any).pricingHistory) ? (vehicle as any).pricingHistory : []
  const baseStart = getActiveLeaseStart(vehicle)
  const baseRecord = {
    startDate: baseStart,
    rentAmount: vehicle.rentAmount,
    rentPeriod: vehicle.rentPeriod,
    rentPeriodMonths: (vehicle as any).rentPeriodMonths,
    reminderAmount: (vehicle as any).reminderAmount,
    reminderPeriod: (vehicle as any).reminderPeriod,
    reminderPeriodMonths: (vehicle as any).reminderPeriodMonths,
    receivableMode: (vehicle as any).receivableMode,
    receivableMonths: (vehicle as any).receivableMonths
  }
  const source = history.length ? history : [baseRecord]
  const map = new Map<string, any>()
  source.forEach((rec: any) => {
    if (!rec || !rec.startDate) return
    const key = dayjs(rec.startDate).format('YYYY-MM-DD')
    const rentNumber = Number(rec.rentAmount)
    const rentAmount = Number.isFinite(rentNumber)
      ? rentNumber
      : parseFloat(String(rec.rentAmount || vehicle.rentAmount || 0))
    map.set(key, {
      startDate: key,
      rentAmount,
      rentPeriod: rec.rentPeriod || vehicle.rentPeriod,
      rentPeriodMonths: rec.rentPeriodMonths,
      reminderAmount: rec.reminderAmount ?? (vehicle as any).reminderAmount,
      reminderPeriod: rec.reminderPeriod || (vehicle as any).reminderPeriod,
      reminderPeriodMonths: rec.reminderPeriodMonths ?? (vehicle as any).reminderPeriodMonths,
      receivableMode: rec.receivableMode || (vehicle as any).receivableMode,
      receivableMonths: rec.receivableMonths ?? (vehicle as any).receivableMonths
    })
  })
  return Array.from(map.values()).sort((a, b) => dayjs(a.startDate).valueOf() - dayjs(b.startDate).valueOf())
}
export const getExpectedTillNow = (vehicle: Vehicle) => {
  if (isReceivableExemptVehicle(vehicle)) return 0
  const records = getPricingRecords(vehicle)
  if (vehicle?.plate) {
    console.log('[Outstanding] pricing records', {
      plate: vehicle.plate,
      records
    })
  }
  if (!records.length) return 0
  const today = dayjs()
  const dueValue = getVehicleDueDate(vehicle, today)
  const due = dueValue ? dayjs(dueValue) : null
  const endDate = due && due.isBefore(today, 'day') ? due : today
  const leaseStart = dayjs(getActiveLeaseStart(vehicle))
  const effectiveStart = leaseStart
  let sum = 0

  records.forEach((rec, idx) => {
    const segStart = dayjs(rec.startDate)
    if (!segStart.isValid()) return
    const nextStart = idx < records.length - 1 ? dayjs(records[idx + 1].startDate) : null
    const segEndRaw = nextStart && nextStart.isValid() ? nextStart.subtract(1, 'day') : endDate
    const segEnd = segEndRaw.isValid() ? segEndRaw : endDate
    if (segEnd.isBefore(segStart, 'day')) return
    const rangeStart = segStart.isAfter(effectiveStart) ? segStart : effectiveStart
    const rangeEnd = segEnd.isBefore(endDate) ? segEnd : endDate
    if (rangeEnd.isBefore(rangeStart, 'day')) return
    sum += getRangeExpectedByRecord(rec, rangeStart, rangeEnd)
  })
  return sum
}

export const getExpectedBetween = (vehicle: Vehicle, startDate: string, endDate: string) => {
  if (isReceivableExemptVehicle(vehicle)) return 0
  const records = getPricingRecords(vehicle)
  if (!records.length) return 0
  const rangeStartInput = dayjs(startDate)
  const rangeEndInput = dayjs(endDate)
  if (!rangeStartInput.isValid() || !rangeEndInput.isValid()) return 0
  let rangeStart = rangeStartInput
  let rangeEnd = rangeEndInput
  if (rangeEnd.isBefore(rangeStart, 'day')) return 0

  const leaseStart = dayjs(getActiveLeaseStart(vehicle))
  if (rangeStart.isBefore(leaseStart, 'day')) {
    rangeStart = leaseStart
  }

  const dueValue = getVehicleDueDate(vehicle, rangeEnd)
  const due = dueValue ? dayjs(dueValue) : null
  if (due && rangeEnd.isAfter(due, 'day')) {
    rangeEnd = due
  }

  if (rangeEnd.isBefore(rangeStart, 'day')) return 0

  let sum = 0
  records.forEach((rec, idx) => {
    const segStart = dayjs(rec.startDate)
    if (!segStart.isValid()) return
    const nextStart = idx < records.length - 1 ? dayjs(records[idx + 1].startDate) : null
    const segEndRaw = nextStart && nextStart.isValid() ? nextStart.subtract(1, 'day') : rangeEnd
    const segEnd = segEndRaw.isValid() ? segEndRaw : rangeEnd
    if (segEnd.isBefore(segStart, 'day')) return
    const sliceStart = segStart.isAfter(rangeStart, 'day') ? segStart : rangeStart
    const sliceEnd = segEnd.isBefore(rangeEnd, 'day') ? segEnd : rangeEnd
    if (sliceEnd.isBefore(sliceStart, 'day')) return

    sum += getRangeExpectedByRecord(rec, sliceStart, sliceEnd)
  })

  return sum
}

const getEffectiveStartDate = (vehicle: Vehicle) => {
  const records = getPricingRecords(vehicle)
  if (!records.length) return dayjs(getActiveLeaseStart(vehicle))
  const leaseStart = dayjs(getActiveLeaseStart(vehicle))
  return leaseStart
}

export const getAggregatesForVehicle = (vehicle: Vehicle, allReceipts: any[]) => {
  const extractItems = (r: any) => {
    const raw = (r as any).items
    if (Array.isArray(raw)) return raw
    if (raw && typeof raw === 'object') return Object.values(raw)
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw)
        if (Array.isArray(parsed)) return parsed
        if (parsed && typeof parsed === 'object') return Object.values(parsed)
      } catch {
      }
    }
    return []
  }

  let paidSum = 0
  let pendingSubmittedSum = 0
  const effectiveStart = getEffectiveStartDate(vehicle)
  allReceipts.forEach(r => {
    const items = extractItems(r)
    items.forEach((it: any) => {
      if (!matchVehicle(it, vehicle)) return
      const itemStartRaw = it.chargeRangeStart || it.periodStart || it.startDate || r.startDate
      const itemEndRaw = it.chargeRangeEnd || it.periodEnd || it.dueDate || r.chargeRangeEnd || r.dueDate
      if (itemStartRaw || itemEndRaw) {
        const itemStart = dayjs(itemStartRaw)
        const itemEnd = dayjs(itemEndRaw)
        if (itemEnd.isValid() && itemEnd.isBefore(effectiveStart, 'day')) return
        if (!itemEnd.isValid() && itemStart.isValid() && itemStart.isBefore(effectiveStart, 'day')) return
      }
      const val = Number(it.receivedAmount ?? it.amount ?? 0) || 0
      if (isPaidItem((r as any).status, it.status)) {
        paidSum += val
      } else {
        pendingSubmittedSum += val
      }
    })
  })
  const expectedSum = getExpectedTillNow(vehicle)
  return { expectedSum, paidSum, pendingSubmittedSum }
}

export const getAggregatesForVehicleAtDate = (vehicle: Vehicle, allReceipts: any[], asOf?: dayjs.Dayjs) => {
  const extractItems = (r: any) => {
    const raw = (r as any).items
    if (Array.isArray(raw)) return raw
    if (raw && typeof raw === 'object') return Object.values(raw)
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw)
        if (Array.isArray(parsed)) return parsed
        if (parsed && typeof parsed === 'object') return Object.values(parsed)
      } catch {
      }
    }
    return []
  }

  const cutoff = asOf?.isValid() ? asOf.endOf('day') : null
  let paidSum = 0
  let pendingSubmittedSum = 0
  const effectiveStart = getEffectiveStartDate(vehicle)

  allReceipts.forEach(r => {
    const eventTime = dayjs((r as any)?.verifiedAt || (r as any)?.accountedAt || (r as any)?.updatedAt || (r as any)?.createdAt)
    if (cutoff && eventTime.isValid() && eventTime.isAfter(cutoff, 'day')) return
    const items = extractItems(r)
    items.forEach((it: any) => {
      if (!matchVehicle(it, vehicle)) return
      const itemStartRaw = it.chargeRangeStart || it.periodStart || it.startDate || r.startDate
      const itemEndRaw = it.chargeRangeEnd || it.periodEnd || it.dueDate || r.chargeRangeEnd || r.dueDate
      if (itemStartRaw || itemEndRaw) {
        const itemStart = dayjs(itemStartRaw)
        const itemEnd = dayjs(itemEndRaw)
        if (itemEnd.isValid() && itemEnd.isBefore(effectiveStart, 'day')) return
        if (!itemEnd.isValid() && itemStart.isValid() && itemStart.isBefore(effectiveStart, 'day')) return
      }
      const val = Number(it.receivedAmount ?? it.amount ?? 0) || 0
      if (isPaidItem((r as any).status, it.status)) {
        paidSum += val
      } else {
        pendingSubmittedSum += val
      }
    })
  })

  const leaseStart = dayjs(getActiveLeaseStart(vehicle))
  const start = leaseStart.isValid() ? leaseStart.format('YYYY-MM-DD') : String(vehicle.startDate || '')
  const end = (cutoff || dayjs()).format('YYYY-MM-DD')
  const expectedSum = getExpectedBetween(vehicle, start, end)
  return { expectedSum, paidSum, pendingSubmittedSum }
}

export const getOutstanding = (
  vehicle: Vehicle,
  allReceipts: any[],
  adjustments: VehicleAdjustment[] = []
) => {
  const { expectedSum, paidSum } = getAggregatesForVehicle(vehicle, allReceipts)
  const { outstandingDelta } = getAdjustmentEffect(vehicle, adjustments)
  return Math.max(expectedSum - paidSum + outstandingDelta, 0)
}

export const getOutstandingAtDate = (
  vehicle: Vehicle,
  allReceipts: any[],
  adjustments: VehicleAdjustment[] = [],
  asOf?: dayjs.Dayjs
) => {
  const { expectedSum, paidSum } = getAggregatesForVehicleAtDate(vehicle, allReceipts, asOf)
  const { outstandingDelta } = getAdjustmentEffectAtDate(vehicle, adjustments, asOf)
  return Math.max(expectedSum - paidSum + outstandingDelta, 0)
}

export const buildPeriods = (vehicle: Vehicle) => {
  if (isReceivableExemptVehicle(vehicle)) return []
  const start = dayjs(getActiveLeaseStart(vehicle)).startOf('month')
  const today = dayjs()
  const dueValue = getVehicleDueDate(vehicle, today)
  const due = dueValue ? dayjs(dueValue) : null
  const end = (due && due.isBefore(today, 'day') ? due : today).endOf('month')
  const periods: { key: string; start: string; end: string; expected: number }[] = []
  let cursor = start.clone()
  while (cursor.isBefore(end) || cursor.isSame(end, 'month')) {
    const key = cursor.format('YYYY-MM')
    const monthStart = cursor.startOf('month')
    const monthEnd = monthStart.endOf('month')
    const activePricing = getPricingRecords(vehicle)
      .filter(r => {
        const start = dayjs(r.startDate)
        return start.isBefore(monthStart, 'day') || start.isSame(monthStart, 'day')
      })
      .sort((a, b) => dayjs(a.startDate).valueOf() - dayjs(b.startDate).valueOf())
      .pop()
    const mode = getReceivableMode(activePricing)
    const monthly = mode === 'monthly_plan'
      ? (getAnnualReceivableAmount(activePricing) / Math.max(getReceivableMonths(activePricing), 1))
      : ((getAnnualReceivableAmount(activePricing) / 365) * monthStart.daysInMonth())
    periods.push({
      key,
      start: monthStart.format('YYYY-MM-DD'),
      end: monthEnd.format('YYYY-MM-DD'),
      expected: monthly
    })
    cursor = cursor.add(1, 'month')
  }
  return periods
}

export const getPaidMapByPeriod = (vehicle: Vehicle, allReceipts: any[]) => {
  const extractItems = (r: any) => {
    const raw = (r as any).items
    if (Array.isArray(raw)) return raw
    if (raw && typeof raw === 'object') return Object.values(raw)
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw)
        if (Array.isArray(parsed)) return parsed
        if (parsed && typeof parsed === 'object') return Object.values(parsed)
      } catch {
      }
    }
    return []
  }

  const map: Record<string, number> = {}
  const activeStart = dayjs(getActiveLeaseStart(vehicle))
  const currentLeaseId = (vehicle as any).currentLeaseId
  allReceipts.forEach(r => {
    const items = extractItems(r)
    items.forEach((it: any) => {
      if (!matchVehicle(it, vehicle)) return
      if (!isPaidItem((r as any).status, it.status)) return
      if (currentLeaseId && it.leaseId && it.leaseId !== currentLeaseId) return
      if (!it.leaseId) {
        const itemStart = dayjs(it.chargeRangeStart || it.periodStart || it.startDate || r.startDate)
        const itemEnd = dayjs(it.chargeRangeEnd || it.periodEnd || it.dueDate || r.chargeRangeEnd || r.dueDate)
        if (itemEnd.isValid() && itemEnd.isBefore(activeStart, 'day')) return
        if (!itemEnd.isValid() && itemStart.isValid() && itemStart.isBefore(activeStart, 'day')) return
      }
      const key = it.periodKey || dayjs(it.dueDate || it.startDate || new Date()).format('YYYY-MM')
      const val = Number(it.receivedAmount ?? it.amount ?? 0) || 0
      map[key] = (map[key] || 0) + val
    })
  })
  return map
}

export const getPeriodDeficits = (vehicle: Vehicle, allReceipts: any[]) => {
  const periods = buildPeriods(vehicle)
  const paidMap = getPaidMapByPeriod(vehicle, allReceipts)
  return periods
    .map(p => {
      const deficit = Math.max(Number(p.expected || 0) - Number(paidMap[p.key] || 0), 0)
      return deficit > 0 ? { ...p, deficit } : null
    })
    .filter(Boolean) as { key: string; start: string; end: string; expected: number; deficit: number }[]
}

export const allocatePaymentByPeriod = (
  vehicle: Vehicle,
  payAmount: number,
  allReceipts: any[],
  customMonths?: number
) => {
  if (payAmount <= 0) {
    return [{
      vehicleId: vehicle._id || vehicle.id,
      leaseId: (vehicle as any).currentLeaseId,
      plate: vehicle.plate,
      expectedAmount: payAmount,
      receivedAmount: payAmount,
      periodKey: dayjs().format('YYYY-MM'),
      dueDate: getVehicleDueDate(vehicle, dayjs()) || dayjs().endOf('month').format('YYYY-MM-DD'),
      startDate: vehicle.startDate || '',
      rentPeriod: vehicle.rentPeriod || 'monthly',
      status: 'submitted'
    }]
  }

  const periods = buildPeriods(vehicle)
  const paidMap = getPaidMapByPeriod(vehicle, allReceipts)
  let remaining = payAmount
  const items: any[] = []

  periods.forEach(p => {
    if (remaining <= 0) return
    const already = paidMap[p.key] || 0
    const deficit = Math.max(p.expected - already, 0)
    if (deficit <= 0) return
    const alloc = Math.min(deficit, remaining)
    items.push({
      vehicleId: vehicle._id || vehicle.id,
      leaseId: (vehicle as any).currentLeaseId,
      plate: vehicle.plate,
      expectedAmount: alloc,
      receivedAmount: alloc,
      periodKey: p.key,
      periodStart: p.start,
      periodEnd: p.end,
      dueDate: p.end,
      startDate: p.start,
      rentPeriod: vehicle.rentPeriod || 'monthly',
      status: 'submitted'
    })
    remaining -= alloc
  })

  if (remaining > 0) {
    const months = customMonths && customMonths > 0 ? customMonths : 1
    const periodStart = dayjs().startOf('month')
    const periodEnd = periodStart.add(months, 'month').endOf('month')
    items.push({
      vehicleId: vehicle._id || vehicle.id,
      leaseId: (vehicle as any).currentLeaseId,
      plate: vehicle.plate,
      expectedAmount: remaining,
      receivedAmount: remaining,
      periodKey: 'advance',
      periodStart: periodStart.format('YYYY-MM-DD'),
      periodEnd: periodEnd.format('YYYY-MM-DD'),
      dueDate: getVehicleDueDate(vehicle, dayjs()) || periodEnd.format('YYYY-MM-DD'),
      startDate: vehicle.startDate || '',
      rentPeriod: vehicle.rentPeriod || 'monthly',
      status: 'submitted'
    })
  }

  return items
}
